"use client";

import { useState, useEffect } from "react";
import { useParams, useApiReady } from "@/hooks";
import { AppDispatch } from "@/store";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPlans,
  createPlans,
  updatePlans,
  deletePlans,
  togglePlanActive,
} from "@/store/modules/planSubscription/thunks";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { planSchema } from "@/validations";
import { UseDataRangeFilter } from "@/types";
import { useAuth } from "@/context/AuthContext";

const usePlanSubscription = ({ dateRange, onSuccess }: UseDataRangeFilter) => {
  const { params, setParams, handlePage, updateParams } = useParams();
  const { per_page, current_page } = params;
  const [searchValue, setSearchValue] = useState<string>(params.search || "");
  const [isActiveFilter, setIsActiveFilter] = useState<number | undefined>(
    // params.filter may be undefined or an object
    // initialize from params.filter.is_active when available
    params &&
      (params as any).filter &&
      (params as any).filter.is_active !== undefined
      ? (params as any).filter.is_active
      : undefined,
  );
  const { currency } = useAuth();

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(planSchema),
    defaultValues: {
      name: "",
      payment_method_id: "",
      limit: null,
      days: JSON.stringify({
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
      }),
      value: null,
      change_existing_registration_price: false,
      description: "",
      service: [],
    },
  });

  const setFormData = (data: Partial<typeof planSchema.__outputType>) => {
    reset(data);
  };

  const dispatch: AppDispatch = useDispatch();
  const [planId, setPlanId] = useState<any>(null);
  const { isReady: isApiReady } = useApiReady(2000);

  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);

  const onSubmit = async (data: Partial<typeof planSchema.__outputType>) => {
    try {
      let result;
      let selectedDays: string[] = [];

      try {
        if (typeof data.days === "string") {
          const parsed = JSON.parse(data.days);
          selectedDays = Object.entries(parsed)
            .filter(([_, val]) => val === true)
            .map(([key]) => key);
        } else if (Array.isArray(data.days)) {
          selectedDays = data.days as string[];
        }
      } catch (e) {
        selectedDays = [];
      }

      const allDays: Record<
        | "monday"
        | "tuesday"
        | "wednesday"
        | "thursday"
        | "friday"
        | "saturday"
        | "sunday",
        boolean
      > = {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
      };

      selectedDays.forEach((day) => {
        if (day in allDays) {
          allDays[day as keyof typeof allDays] = true;
        }
      });

      const basePayload: any = {
        ...data,
        days: JSON.stringify(allDays),
      };

      let payload: any = basePayload;
      // Se houver imagem, usamos FormData
      if (pendingImageFile) {
        const fd = new FormData();
        Object.entries(basePayload).forEach(([k, v]) => {
          if (v === undefined || v === null) return;
          if (k === "service" && Array.isArray(v)) {
            v.forEach((srv: any, idx: number) => {
              if (srv.service_id !== undefined)
                fd.append(
                  `service[${idx}][service_id]`,
                  String(srv.service_id),
                );
              if (srv.duration !== undefined)
                fd.append(`service[${idx}][duration]`, String(srv.duration));
              if (
                srv.points !== undefined &&
                srv.points !== null &&
                srv.points !== ""
              )
                fd.append(`service[${idx}][points]`, String(srv.points));
            });
            return;
          }
          if (k === "days" && typeof v !== "string") {
            fd.append(k, JSON.stringify(v));
            return;
          }
          fd.append(k, typeof v === "string" ? v : String(v));
        });
        fd.append("image", pendingImageFile);
        payload = fd;
      }

      if (planId) {
        if (payload instanceof FormData) {
          (payload as FormData).append("_method", "PUT");
          result = await dispatch(
            updatePlans({ id: planId, data: payload } as any),
          );
        } else {
          // Garantir que service seja array bruto (não string) para JSON
          if (
            (payload as any).service &&
            typeof (payload as any).service === "string"
          ) {
            try {
              (payload as any).service = JSON.parse((payload as any).service);
            } catch {}
          }
          result = await dispatch(updatePlans({ id: planId, ...payload }));
        }
      } else {
        result = await dispatch(createPlans(payload));
      }

      if (
        createPlans.fulfilled.match(result) ||
        updatePlans.fulfilled.match(result)
      ) {
        if (onSuccess) onSuccess();
        dispatch(fetchPlans({ ...params, per_page: 200 }));
        reset();
        setPlanId(null);
        setPendingImageFile(null);
      }
    } catch (error) {
      console.error("Erro ao enviar dados do plano:", error);
    }
  };

  // Estado local para efeito otimista
  const reduxPlans = useSelector((state: any) => state.planSubscription);
  const [optimisticPlans, setOptimisticPlans] = useState<any[]>([]);

  // Sincroniza estado local com redux
  useEffect(() => {
    if (reduxPlans?.planSubscription?.data) {
      setOptimisticPlans(reduxPlans.planSubscription.data);
    }
  }, [reduxPlans?.planSubscription?.data]);
  const loading = useSelector((state: any) => state.planSubscription.loading);
  const error = useSelector((state: any) => state.planSubscription.error);

  useEffect(() => {
    if (!isApiReady) return;
    dispatch(fetchPlans({ ...params, per_page: 200 }));
  }, [isApiReady, params, dispatch]);

  const handleSubmitFilter = () => {
    const payload: Record<string, any> = {
      ...params,
      start_date: dateRange.start.toISOString().split("T")[0],
      end_date: dateRange.end.toISOString().split("T")[0],
    };

    dispatch(fetchPlans({ ...payload, per_page: 200 }));
  };

  const handleDelete = async () => {
    try {
      if (planId) {
        await dispatch(deletePlans(planId));
        setPlanId(null);
        return true;
      }
    } catch (error) {
      console.error("Erro ao excluir plano:", error);
    }
  };

  // Debounce para busca
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue !== params.search) {
        updateParams({
          search: searchValue,
          current_page: 1,
        });
        setCurrentPage(1);
      }
    }, 500); // 500ms de debounce

    return () => clearTimeout(timeoutId);
  }, [searchValue, updateParams, params.search]);

  const [currentPage, setCurrentPage] = useState(
    reduxPlans?.planSubscription?.current_page ?? 1,
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handlePage(page);
  };

  const handleFilterChange = (value: string) => {
    setSearchValue(value);
  };

  const handleChangeIsActive = (value: number | undefined) => {
    setIsActiveFilter(value);
    const newFilter = { ...(params.filter || {}) } as Record<string, any>;
    if (value === undefined || value === null) {
      delete newFilter.is_active;
    } else {
      newFilter.is_active = value;
    }
    updateParams({ filter: newFilter, current_page: 1 });
    setCurrentPage(1);
  };

  const totalItems = reduxPlans?.planSubscription?.total ?? 0;
  const itemsPerPage = reduxPlans?.planSubscription?.per_page ?? 10;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;
  const pagesArray = Array.from({ length: totalPages }, (_, i) => i + 1);

  useEffect(() => {
    if (
      reduxPlans?.planSubscription?.current_page &&
      reduxPlans?.planSubscription?.current_page !== currentPage
    ) {
      setCurrentPage(reduxPlans?.planSubscription?.current_page);
    }
  }, [reduxPlans?.planSubscription?.current_page]);

  // Toggle ativo/inativo com efeito otimista
  const handleToggleActive = async (id: string) => {
    // Encontra plano e alterna localmente
    setOptimisticPlans((prev: any[]) =>
      prev.map((plan) =>
        plan.id === id
          ? { ...plan, is_active: plan.is_active === 1 ? 0 : 1 }
          : plan,
      ),
    );
    try {
      const result = await dispatch(togglePlanActive(id));
      if (togglePlanActive.fulfilled.match(result)) {
        // Mantém estado otimista e atualiza lista do redux
        dispatch(fetchPlans({ ...params, per_page: 200 }));
      } else {
        // Reverte se falhar
        setOptimisticPlans((prev: any[]) =>
          prev.map((plan) =>
            plan.id === id
              ? {
                  ...plan,
                  is_active: plan.is_active === 1 ? 0 : 1,
                }
              : plan,
          ),
        );
      }
    } catch (error) {
      // Reverte se erro
      setOptimisticPlans((prev: any[]) =>
        prev.map((plan) =>
          plan.id === id
            ? { ...plan, is_active: plan.is_active === 1 ? 0 : 1 }
            : plan,
        ),
      );
      console.error("Erro ao alternar status do plano:", error);
    }
  };

  return {
    register,
    handleSubmit,
    onSubmit,
    reset,
    errors,
    isSubmitting,
    plans: {
      ...reduxPlans,
      planSubscription: {
        ...reduxPlans.planSubscription,
        data: optimisticPlans,
      },
    },
    loading,
    error,
    setPlanId,
    handleDelete,
    setFormData,
    getValues,
    setValue,
    control,
    handleSubmitFilter,
    currentPage,
    pagesArray,
    handlePageChange,
    totalItems,
    itemsPerPage,
    handleFilterChange,
    searchValue,
    watch,
    pendingImageFile,
    setPendingImageFile,
    handleToggleActive,
    isActiveFilter,
    handleChangeIsActive,
    currency,
  };
};

export default usePlanSubscription;
